import { useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import InsightsIcon from '@mui/icons-material/Insights';
import axiosInstance from '../../lib/axios';
import { getUser } from '../../utils/auth';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { BlurredFeatureOverlay } from '../../components/BlurredFeatureOverlay';
import { AnalyticsHeader } from '../../components/analytics/AnalyticsHeader';
import {
  SalesForecastResponse,
  PeriodType,
  TrendDirection,
} from '../../types/analytics';

interface ForecastParams {
  organizationId?: string;
  locationId?: string;
  periodType: PeriodType;
  forecastPeriods: number;
  startDate?: string;
  endDate?: string;
}

const fetchSalesForecast = async (params: ForecastParams): Promise<SalesForecastResponse> => {
  const user = getUser();
  const queryParams: Record<string, any> = {
    periodType: params.periodType,
    forecastPeriods: params.forecastPeriods,
  };

  if (params.startDate) {
    queryParams.startDate = params.startDate;
  }
  if (params.endDate) {
    queryParams.endDate = params.endDate;
  }
  if (user?.locationId) {
    queryParams.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/analytics/forecast', { params: queryParams });
  return response.data;
};

const getTrendIcon = (trend: TrendDirection) => {
  switch (trend) {
    case TrendDirection.UPWARD:
      return <TrendingUpIcon color="success" />;
    case TrendDirection.DOWNWARD:
      return <TrendingDownIcon color="error" />;
    default:
      return <TrendingFlatIcon color="action" />;
  }
};

const getTrendColor = (trend: TrendDirection): 'success' | 'error' | 'default' => {
  switch (trend) {
    case TrendDirection.UPWARD:
      return 'success';
    case TrendDirection.DOWNWARD:
      return 'error';
    default:
      return 'default';
  }
};

const getTrendLabel = (trend: TrendDirection): string => {
  switch (trend) {
    case TrendDirection.UPWARD:
      return 'Upward Trend';
    case TrendDirection.DOWNWARD:
      return 'Downward Trend';
    default:
      return 'Stable';
  }
};

export const SalesForecast = () => {
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const { hasAccess, isLoading: moduleLoading } = useModuleAccess('advanced_analytics');

  // Date range state - default to last 30 days
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('30d');
  const [periodType, setPeriodType] = useState<PeriodType>(PeriodType.DAILY);
  const [forecastPeriods, setForecastPeriods] = useState<number>(14);

  const {
    data: forecastData,
    isLoading,
    error,
    dataUpdatedAt,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['salesForecast', startDate, endDate, periodType, forecastPeriods],
    queryFn: () =>
      fetchSalesForecast({
        periodType,
        forecastPeriods,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      }),
    enabled: hasAccess && !moduleLoading,
    staleTime: 5 * 60 * 1000,       // Data fresh for 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  const handlePresetClick = (preset: string) => {
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case '7d':
        start.setDate(start.getDate() - 7);
        setForecastPeriods(7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        setForecastPeriods(14);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        setForecastPeriods(30);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        setForecastPeriods(90);
        break;
    }

    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const handlePeriodTypeChange = (type: PeriodType) => {
    setPeriodType(type);
    // Adjust forecast periods based on period type
    switch (type) {
      case PeriodType.DAILY:
        setForecastPeriods(14);
        break;
      case PeriodType.WEEKLY:
        setForecastPeriods(8);
        break;
      case PeriodType.MONTHLY:
        setForecastPeriods(6);
        break;
      default:
        setForecastPeriods(14);
    }
  };

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: theme.radius.xl,
    p: 3,
    transition: theme.transitions.normal,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDaysRange = () => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Handle module access loading
  if (moduleLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  // Prepare chart data with confidence intervals
  const chartData =
    forecastData?.forecastData?.map((point) => ({
      period: point.period,
      predicted: point.predictedValue,
      upperBound: point.upperBound,
      lowerBound: point.lowerBound,
      confidence: point.confidence,
    })) || [];

  const pageContent = (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Page Header */}
      <AnalyticsHeader
        category="Sales Analytics"
        title="Sales Forecast"
        subtitle={`Predictive sales analytics with confidence intervals - ${getDaysRange()} days of historical data`}
        dataUpdatedAt={dataUpdatedAt}
        isRefetching={isRefetching}
        onRefresh={() => refetch()}
      />

      {/* Date Range Picker */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Time Period
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
          <ButtonGroup
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: 'none',
                fontWeight: typography.fontWeight.medium,
                fontSize: typography.fontSize.sm,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: palette.primaryMuted,
                },
              },
            }}
          >
            {['7d', '30d', '90d', '1y'].map((preset) => (
              <Button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                sx={{
                  ...(selectedPreset === preset && {
                    backgroundColor: palette.primaryMuted,
                    borderColor: `${palette.primary} !important`,
                    color: palette.primary,
                  }),
                }}
              >
                {preset === '7d' && 'Last 7 days'}
                {preset === '30d' && 'Last 30 days'}
                {preset === '90d' && 'Last 90 days'}
                {preset === '1y' && 'Last year'}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Start Date"
              value={formatDateForInput(startDate)}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              to
            </Typography>
            <TextField
              type="date"
              label="End Date"
              value={formatDateForInput(endDate)}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Period Type Selector */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Forecast Granularity
          </Typography>
        </Box>
        <ButtonGroup
          variant="outlined"
          sx={{
            '& .MuiButton-root': {
              borderColor: colors.border,
              color: colors.textSecondary,
              textTransform: 'none',
              fontWeight: typography.fontWeight.medium,
              fontSize: typography.fontSize.sm,
              '&:hover': {
                borderColor: palette.primary,
                backgroundColor: palette.primaryMuted,
              },
            },
          }}
        >
          {[PeriodType.DAILY, PeriodType.WEEKLY, PeriodType.MONTHLY].map((type) => (
            <Button
              key={type}
              onClick={() => handlePeriodTypeChange(type)}
              sx={{
                ...(periodType === type && {
                  backgroundColor: palette.primaryMuted,
                  borderColor: `${palette.primary} !important`,
                  color: palette.primary,
                }),
              }}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: palette.primary }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box p={3}>
          <Alert
            severity="error"
            sx={{
              borderRadius: theme.radius.lg,
              backgroundColor: palette.errorMuted,
              color: palette.error,
            }}
          >
            {error instanceof Error ? error.message : 'Failed to load forecast data'}
          </Alert>
        </Box>
      )}

      {/* Data Loaded */}
      {!isLoading && !error && forecastData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.primary,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Total Forecasted
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: colors.textPrimary,
                        mt: 1,
                      }}
                    >
                      {formatCurrency(forecastData.totalPredictedValue)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {forecastData.forecastPeriod.periods} periods ahead
                    </Typography>
                  </Box>
                  <ShowChartIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.info,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Average Predicted
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: palette.info,
                        mt: 1,
                      }}
                    >
                      {formatCurrency(forecastData.averagePredictedValue)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      per {periodType}
                    </Typography>
                  </Box>
                  <InsightsIcon sx={{ fontSize: 40, color: alpha(palette.info, 0.2) }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor:
                      forecastData.trendDirection === TrendDirection.UPWARD
                        ? palette.success
                        : forecastData.trendDirection === TrendDirection.DOWNWARD
                        ? palette.error
                        : palette.warning,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Trend Direction
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Chip
                        icon={getTrendIcon(forecastData.trendDirection)}
                        label={getTrendLabel(forecastData.trendDirection)}
                        color={getTrendColor(forecastData.trendDirection)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 0.5 }}>
                      Strength: {(forecastData.trendStrength * 100).toFixed(0)}%
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.accent,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Historical Data
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: palette.accent,
                        mt: 1,
                      }}
                    >
                      {forecastData.historicalPeriod.dataPoints}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      data points analyzed
                    </Typography>
                  </Box>
                  <TimelineIcon sx={{ fontSize: 40, color: alpha(palette.accent, 0.2) }} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Forecast Chart */}
          <Box sx={cardStyles}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Sales Forecast Trend
            </Typography>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorPredicted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={palette.primary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={palette.primary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={palette.info} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={palette.info} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis
                    dataKey="period"
                    stroke={colors.textMuted}
                    tickFormatter={formatDate}
                    style={{ fontSize: typography.fontSize.xs }}
                  />
                  <YAxis stroke={colors.textMuted} style={{ fontSize: typography.fontSize.xs }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: theme.radius.md,
                      color: colors.textPrimary,
                    }}
                    labelStyle={{ color: colors.textPrimary }}
                    formatter={(value: any, name: string) => {
                      if (name === 'predicted') return [formatCurrency(value), 'Forecast'];
                      if (name === 'upperBound') return [formatCurrency(value), 'Upper Bound'];
                      if (name === 'lowerBound') return [formatCurrency(value), 'Lower Bound'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => formatDate(label)}
                  />
                  <Legend
                    wrapperStyle={{ color: colors.textPrimary }}
                    formatter={(value) => {
                      if (value === 'predicted') return 'Forecast';
                      if (value === 'upperBound') return 'Upper Bound (95%)';
                      if (value === 'lowerBound') return 'Lower Bound (95%)';
                      return value;
                    }}
                  />
                  {/* Confidence band - lower bound */}
                  <Area
                    type="monotone"
                    dataKey="lowerBound"
                    stroke={palette.info}
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    fillOpacity={0}
                    fill="transparent"
                  />
                  {/* Confidence band - upper bound */}
                  <Area
                    type="monotone"
                    dataKey="upperBound"
                    stroke={palette.info}
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    fillOpacity={1}
                    fill="url(#colorConfidence)"
                  />
                  {/* Main prediction line */}
                  <Area
                    type="monotone"
                    dataKey="predicted"
                    stroke={palette.primary}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorPredicted)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 400,
                }}
              >
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  No forecast data available. Try adjusting the date range or period type.
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Empty State */}
      {!isLoading && !error && !forecastData && (
        <Box sx={cardStyles}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <ShowChartIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
              No Forecast Data Available
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', maxWidth: 400 }}>
              There isn&apos;t enough historical sales data to generate a forecast. Continue making
              sales and check back later.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  // Wrap content with BlurredFeatureOverlay for non-PROFESSIONAL users
  return (
    <BlurredFeatureOverlay feature="Sales Forecast" requiredTier="professional">
      {pageContent}
    </BlurredFeatureOverlay>
  );
};

export default SalesForecast;
